import React, { useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import LayoutWithSidebar from 'components/v2/LayoutWithSidebar';
import { showToast } from 'stores/ToastStore';
import { findUsers } from 'utils/UserWebAPIUtils';
import Pagination from 'components/v2/PaginationV2';
import AddButton from 'components/v2/buttons/AddButton';
import Table from 'components/v2/Table';
import { getYards, searchYardsByNameAndAddress } from 'utils/YardAPIUtils';
import Spinner from 'components/ui/Spinner';
import MarketsAPIUtils from 'utils/MarketsAPIUtils';
import CargomaticInput from 'components/v2/input';
import { Actions, createYardsListColumns, breadcrumbs, ITEMS_PER_PAGE_LIMIT, SEARCH_DELAY, setYardsListHeaders, } from './constants';
const Loading = function () {
    return (React.createElement("div", { "data-testid": "yardsListLoading", className: "absolute justify-center w-full", style: { top: '50%' } },
        React.createElement("div", { className: "align-middle" },
            React.createElement("span", { className: "text-5xl text-green" },
                React.createElement(Spinner, { showMessage: false })))));
};
const transformYards = (yardsResponse, markets) => {
    const { total, yards } = yardsResponse;
    return {
        total,
        yards: yards.map((yard) => {
            var _a;
            return Object.assign(Object.assign({}, yard), { marketCode: (_a = markets.find((market) => market.id === yard.marketId)) === null || _a === void 0 ? void 0 : _a.code });
        }),
    };
};
const initialState = {
    payload: {
        sortBy: 'nameInternal',
        sortOrder: 'ascend',
        skip: null,
        limit: ITEMS_PER_PAGE_LIMIT,
        yardInput: { id: null, marketId: null, nameInternal: null, status: null },
    },
    activePage: 1,
    yardsResponse: { yards: [], total: 0 },
    searchText: null,
    isLoading: true,
    markets: [],
};
const reducer = (state, action) => {
    switch (action.type) {
        case Actions.SET_IS_LOADING:
            return Object.assign(Object.assign({}, state), { isLoading: action.payload });
        case Actions.SET_YARDS_RESPONSE:
            return Object.assign(Object.assign({}, state), { yardsResponse: action.payload, isLoading: false });
        case Actions.SET_ACTIVE_PAGE:
            return Object.assign(Object.assign({}, state), { activePage: action.payload });
        case Actions.SET_FETCH_PAYLOAD:
            return Object.assign(Object.assign({}, state), { payload: action.payload });
        case Actions.SET_SEARCH_INPUT_VALUE:
            return Object.assign(Object.assign({}, state), { activePage: 1, searchText: action.payload, payload: Object.assign(Object.assign({}, state.payload), { skip: null }) });
        case Actions.SET_MARKETS_RESPONSE:
            return Object.assign(Object.assign({}, state), { markets: action.payload, isLoading: false });
        default:
            return state;
    }
};
export default function YardsList() {
    const { t } = useTranslation();
    const history = useHistory();
    const [yardsState, dispatch] = useReducer(reducer, initialState, undefined);
    const [vendors, setVendors] = useState({});
    const searchYards = async (searchText, sortBy, sortOrder, offset, limit) => {
        dispatch({ type: Actions.SET_IS_LOADING, payload: true });
        const yardsResponse = await searchYardsByNameAndAddress(searchText, sortBy, sortOrder, offset, limit);
        dispatch({
            type: Actions.SET_YARDS_RESPONSE,
            payload: transformYards(yardsResponse.data.searchYardsByNameAndAddress, yardsState.markets),
        });
    };
    const fetchYards = async (sortBy, sortOrder, skip, limit, yardInput = { id: null, marketId: null, nameInternal: null, status: null }) => {
        dispatch({ type: Actions.SET_IS_LOADING, payload: true });
        const yardsResponse = await getYards(sortBy, sortOrder, skip, limit, yardInput);
        dispatch({
            type: Actions.SET_YARDS_RESPONSE,
            payload: transformYards(yardsResponse.data.getYards, yardsState.markets),
        });
    };
    useEffect(() => {
        async function fetchData() {
            const res = await MarketsAPIUtils.getMarketsV2();
            dispatch({
                type: Actions.SET_MARKETS_RESPONSE,
                payload: res,
            });
        }
        fetchData();
    }, []);
    useEffect(() => {
        const { searchText, payload, markets } = yardsState;
        if (markets.length) {
            if (!searchText) {
                fetchYards(...Object.values(yardsState.payload));
            }
            else {
                const { sortBy, sortOrder, limit } = payload;
                const timeOutId = setTimeout(() => searchYards(searchText, sortBy, sortOrder, payload.skip, limit), SEARCH_DELAY);
                return () => clearTimeout(timeOutId);
            }
        }
    }, [yardsState.searchText, yardsState.payload, yardsState.markets]);
    useEffect(() => {
        fetchUsers();
    }, [yardsState.yardsResponse.yards]);
    async function fetchUsers() {
        var _a;
        dispatch({ type: Actions.SET_IS_LOADING, payload: true });
        const venderIds = (_a = yardsState === null || yardsState === void 0 ? void 0 : yardsState.yardsResponse) === null || _a === void 0 ? void 0 : _a.yards.map((yard) => yard === null || yard === void 0 ? void 0 : yard.vendor);
        try {
            if (venderIds.length) {
                const { entity: { items }, } = await findUsers({ _id: venderIds });
                const vendors = items.reduce((_vendors, _vendor) => {
                    _vendors[_vendor._id] = {
                        account_name: _vendor._type === 'Carrier' ? _vendor.name : _vendor.account_name,
                    };
                    return _vendors;
                }, {});
                setVendors(vendors);
            }
        }
        catch (error) {
            showToast(t('NOT_FOUND'), t('USERS_NOT_FOUND'), 'error');
        }
        finally {
            dispatch({ type: Actions.SET_IS_LOADING, payload: false });
        }
    }
    const handleInputChange = (val) => {
        dispatch({
            type: Actions.SET_SEARCH_INPUT_VALUE,
            payload: val,
        });
    };
    const handlePageChange = (eventKey) => {
        dispatch({ type: Actions.SET_ACTIVE_PAGE, payload: eventKey });
        const { searchText, payload } = yardsState;
        const skip = ITEMS_PER_PAGE_LIMIT * (eventKey - 1);
        if (searchText) {
            const { sortBy, sortOrder, limit } = payload;
            searchYards(searchText, sortBy, sortOrder, skip, limit);
            return;
        }
        dispatch({
            type: Actions.SET_FETCH_PAYLOAD,
            payload: Object.assign(Object.assign({}, payload), { skip }),
        });
    };
    const renderFooter = () => {
        return (React.createElement("tr", null,
            React.createElement("td", { colSpan: 8, className: "p-0" },
                React.createElement(Pagination, { className: "border-t-0", perPage: ITEMS_PER_PAGE_LIMIT, page: yardsState.activePage, pages: Math.ceil(yardsState.yardsResponse.total / ITEMS_PER_PAGE_LIMIT), total: yardsState.yardsResponse.total, showCount: true, paginationContainerClassName: "mx-auto my-0 sticky right-2/4", onPageClick: (eventKey) => handlePageChange(eventKey) }))));
    };
    return (React.createElement(LayoutWithSidebar, { className: "pt-3", title: t('YARD_MANAGEMENT'), breadcrumbs: breadcrumbs },
        React.createElement("div", { className: "flex items-center space-x-3 mb-4" },
            React.createElement("h3", null, t('MANAGE_YARDS')),
            React.createElement("div", { "data-testid": "addNewYardBtnWrapper", className: "flex justify-end" },
                React.createElement(AddButton, { onClick: () => history.push('/yards/new') }))),
        React.createElement("div", { className: "mt-4 mb-2" },
            React.createElement(CargomaticInput, { type: "search", label: t('TOTAL_SHIPMENT_WEIGHT'), placeholder: t('TOTAL_SHIPMENT_WEIGHT'), onChange: (val) => handleInputChange(val) })),
        React.createElement("div", { className: "overflow-x-auto table-wrapper" }, yardsState.isLoading ? (React.createElement(Loading, null)) : (React.createElement(Table, { headers: setYardsListHeaders(t), columns: createYardsListColumns(history, t, vendors), data: yardsState.yardsResponse.yards, footer: renderFooter })))));
}
